import { FormattedMessage, useIntl } from 'react-intl';
import { Link as RouterLink } from 'react-router';

import { CarryOutOutlined, RightOutlined } from '@ant-design/icons';

import { alpha, useTheme } from '@mui/material/styles';
import { Box, IconButton, Link, Tooltip, Typography } from '@mui/material';

import { useGetMenuMaster } from '@trendiolive/trendio-selfcare-ui-kit';

import useTTSOnboarding from 'features/tts-onboarding/hooks/useTTSOnboarding';

import type { TtsOnboardingActiveStepType } from 'model/types/tts-onboarding';

import { APP_DEFAULT_PATH } from 'constants/appConstants';
import { BILLING_PAYMENT_METHODS_PATH, PRICING_PATH } from 'constants/routeConstants';

// ==============================|| TTS ONBOARDING — NAV (COMPACT) ||============================== //

export default function TTSOnboardingNavStatus() {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const { isVisible, activeOnboardingStep } = useTTSOnboarding();

  const step: TtsOnboardingActiveStepType | null = activeOnboardingStep;

  if (!isVisible || step == null) {
    return null;
  }

  const stepConfig = {
    payment: {
      to: BILLING_PAYMENT_METHODS_PATH,
      captionId: 'tts-onboarding-nav-payment-caption' as const,
      ariaId: 'tts-onboarding-nav-payment-aria' as const
    },
    plan: {
      to: PRICING_PATH,
      captionId: 'tts-onboarding-nav-plan-caption' as const,
      ariaId: 'tts-onboarding-nav-plan-aria' as const
    },
    permissions: {
      to: APP_DEFAULT_PATH,
      captionId: 'tts-onboarding-nav-permissions-caption' as const,
      ariaId: 'tts-onboarding-nav-permissions-aria' as const
    }
  }[step];

  const aria = formatMessage({ id: stepConfig.ariaId });
  const tooltipTitle = formatMessage({ id: 'tts-onboarding-nav-tooltip' }, { title: formatMessage({ id: 'tts-onboarding-nav-cta' }) });

  const surfaceSx = {
    bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.08),
    border: 1,
    borderColor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.24 : 0.14),
    borderRadius: 1.5,
    m: 1,
    px: drawerOpen ? 2 : 1.25,
    py: 2
  };

  if (!drawerOpen) {
    return (
      <Box sx={{ ...surfaceSx, display: 'flex', justifyContent: 'center', py: 1 }}>
        <Tooltip title={tooltipTitle}>
          <IconButton aria-label={aria} color="primary" component={RouterLink} size="small" to={stepConfig.to}>
            <CarryOutOutlined />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box aria-label={formatMessage({ id: 'tts-onboarding-nav-region-aria' })} sx={surfaceSx}>
      <Typography component="h2" fontWeight={700} variant="h6" sx={{ textWrap: 'auto', lineHeight: 1.3, mb: 1 }}>
        <FormattedMessage id="tts-onboarding-nav-title" />
      </Typography>
      <Typography color="text.secondary" component="p" sx={{ lineHeight: 1.35, mb: 0.75, m: 0, textWrap: 'auto' }} variant="caption">
        <Box
          component="span"
          sx={(t) => ({
            color: t.palette.text.primary,
            fontSize: t.typography.caption.fontSize,
            fontWeight: 700,
            lineHeight: t.typography.caption.lineHeight
          })}
        >
          <FormattedMessage id="tts-onboarding-nav-missing-question" />
        </Box>{' '}
        <Box
          component="span"
          sx={(t) => ({
            fontSize: t.typography.caption.fontSize,
            fontWeight: t.typography.caption.fontWeight,
            lineHeight: t.typography.caption.lineHeight
          })}
        >
          <FormattedMessage id={stepConfig.captionId} />
        </Box>
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
        <Link
          aria-label={aria}
          color="primary"
          component={RouterLink}
          sx={(theme) => ({
            ...theme.typography.body2,
            alignItems: 'center',
            display: 'inline-flex',
            fontWeight: 900,
            gap: 0.5,
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline'
            }
          })}
          to={stepConfig.to}
          underline="none"
        >
          <FormattedMessage id={'tts-onboarding-nav-cta'} />
          <RightOutlined aria-hidden style={{ fontSize: '0.9em' }} />
        </Link>
      </Box>
    </Box>
  );
}
